import React, { Fragment, memo, useMemo, useState } from 'react';
import Div from '@/shared/Div';
import Button from '@/shared/Button';
import Heading from '@/shared/Heading';
import ChevronDownIcon from '@/components/Icons/ChevronDownIcon';
import ChevronUpIcon from '@/components/Icons/ChevronUpIcon';
import Span from '@/shared/Span';
import is from '@/utils/is';
import Container from '../Container';

interface AccordionItem {
  title: string;
  content: string;
}

export interface AccordionProps {
  title: string;
  list: AccordionItem[];
}

const Accordion = ({ title, list }: AccordionProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const faqStructuredData = useMemo(() => {
    if (!is.empty.array(list)) {
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: list.map(({ title, content }: any, index: number) => ({
          '@type': 'Question',
          name: title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: content,
          },
        })),
      };
    }

    return {};
  }, [list]);

  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  if (is.empty.array(list)) return null;
  return (
    <Fragment>
      <Div className="bg-cm-subtle py-s6 md:py-s10 ">
        <Container>
          {title && (
            <Heading
              level={2}
              className="text-h-3 md:text-hd-2 border-b border-cti-on-secondary pb-s4"
            >
              {title}
            </Heading>
          )}
          {list?.map((item, index) => (
            <Div key={index} className="border-b border-cti-on-secondary">
              <Button
                className="w-full flex !items-start justify-between text-hd-5 md:text-hd-4 py-s4 md:py-s5 !text-left"
                theme="none"
                dataTestId={`accordion-button-${index + 1}`}
                onClick={() => toggleAccordion(index)}
              >
                <Heading level={3} className="w-[calc(100%-24px)]">
                  {item.title}
                </Heading>
                {activeIndex === index ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </Button>
              {activeIndex === index && (
                <Div className="pb-s5 text-body-base font-primary">{item.content}</Div>
              )}
            </Div>
          ))}
        </Container>
      </Div>
      <script
        id="faq"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqStructuredData),
        }}
      />
    </Fragment>
  );
};

export default memo(Accordion);
